/** Unsharp Mask — classic detail enhancement.
 *
 *  Formula: `out = source + (amount/100) * (source - blur(source))`
 *  with a per-channel threshold gate so flat regions don't amplify
 *  sensor noise. The Gaussian blur is the same separable
 *  implementation as `filter-gaussian-blur`; we import its
 *  `applyGaussianBlurPixels` rather than reimplementing.
 *
 *  Per-channel (not luminance) sharpening matches Photoshop's
 *  Unsharp Mask behavior and the user expectation for the filter
 *  named "Unsharp Mask". Color fringing on saturated edges is a
 *  feature here, not a bug — users who want fringe-free sharpening
 *  reach for Smart Sharpen instead. */

import { applyGaussianBlurPixels } from './gaussian-blur';

export interface UnsharpMaskValue {
  /** Sharpening strength as a percentage. 0 = identity, 100 = full
   *  difference added back, larger values over-sharpen (Photoshop
   *  allows up to 500%). */
  amount: number;
  /** Gaussian blur radius (px) used to compute the unsharp
   *  difference. Larger radius = broader haloing around edges. */
  radius: number;
  /** Minimum per-channel tonal difference (0..255) for sharpening
   *  to apply. Pixels whose `|source - blur|` is below this are
   *  passed through unchanged — protects smooth regions from
   *  having their grain amplified. */
  threshold: number;
}

export const identityUnsharpMaskValue = (): UnsharpMaskValue => ({
  amount: 0,
  // radius 1px only sharpens single-pixel detail, which is imperceptible
  // on today's high-resolution (phone-camera) images — "full strength"
  // amount then still looks very light. 3px is a more useful default for
  // high-DPI sources; the slider still spans the full range either way.
  // (amount: 0 keeps this an identity on open regardless of radius.)
  radius: 3,
  threshold: 0,
});

export function applyUnsharpMaskPixels(source: ImageData, value: UnsharpMaskValue): ImageData {
  const w = source.width;
  const h = source.height;
  const amount = Math.max(0, value.amount);
  const radius = Math.max(0, value.radius);
  if (amount === 0 || radius === 0) {
    return new ImageData(new Uint8ClampedArray(source.data), w, h);
  }
  const threshold = Math.max(0, Math.min(255, value.threshold));
  const k = amount / 100;
  const blurred = applyGaussianBlurPixels(source, { radius });
  const src = source.data;
  const blr = blurred.data;
  const out = new Uint8ClampedArray(src.length);
  for (let i = 0; i < src.length; i += 4) {
    for (let c = 0; c < 3; c++) {
      const s = src[i + c];
      const b = blr[i + c];
      const diff = s - b;
      if (Math.abs(diff) >= threshold) {
        out[i + c] = s + k * diff;
      } else {
        out[i + c] = s;
      }
    }
    out[i + 3] = src[i + 3];
  }
  return new ImageData(out, w, h);
}

export function applyUnsharpMaskToCanvas(
  source: ImageData,
  target: HTMLCanvasElement,
  value: UnsharpMaskValue,
): void {
  const tCtx = target.getContext('2d');
  if (!tCtx) return;
  if (target.width !== source.width || target.height !== source.height) {
    target.width = source.width;
    target.height = source.height;
  }
  tCtx.putImageData(applyUnsharpMaskPixels(source, value), 0, 0);
}
